import React, { Fragment, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { uploadImage } from "services"
import { resizeImage } from "utils"
import { Button, Tooltip } from "components"
import classes from "./FuwfFooter.module.scss"

const FuwfFooter = props => {
  const {
    setTab,
    files,
    setFiles,
    sending,
    setSending,
    setOpenModal,
    maxFilesReached,
    maxNumberOfUploads,
    onUploadComplete,
    imageCategory,
    preserveOriginalFile
  } = props

  const [t] = useTranslation()

  const [failedUploads, setFailedUploads] = useState([])
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [haveFailedUploads, setHaveFailedUploads] = useState(false)

  const unmount = useRef(false)

  const handleDeselect = () => {
    setTab("device")

    setFiles([])
  }

  const uploadFailed = (file) => {
    setFiles(prev =>
      prev.map(f =>
        f.id === file.id
          ?
          {
            ...f,
            uploadProgress: 0,
            uploadFailed: true
          }
          :
          f
      )
    )
  }

  const handleUploadProgress =
    useCallback((progressEvent, file) => {
      const percentCompleted =
        Math.round(progressEvent.loaded / progressEvent.total)

      setFiles(prev =>
        prev.map(f =>
          f.id === file.id
            ?
            {
              ...f,
              uploadProgress: percentCompleted
            }
            :
            f
        )
      )
    }, [])

  const reset = (files) => {
    files.forEach(file => {
      setFiles(prev =>
        prev.map(f =>
          f.id === file.id
            ?
            {
              ...f,
              uploadProgress: 0,
              uploadFailed: false
            }
            :
            f
        )
      )
    })
  }

  const handleUploadFiles = async (files) => {
    reset(files)

    setSending(true)

    const results = [...uploadedFiles]

    const fails = []

    for (const file of files) {
      if (!file.isFile) {
        results.push(file.url)

        continue
      }

      try {
        const shouldUploadOriginalFile =
          preserveOriginalFile &&
          ["image/png", "image/webp"].includes(file.type)

        const resizedFile =
          shouldUploadOriginalFile
            ? {
              blob: file.blob,
              name: file.name
            }
            : await resizeImage(file.blob)

        if (unmount.current) return

        const form = new FormData()

        form.append("qqfile", resizedFile.blob, resizedFile.name)

        const response =
          await uploadImage(
            form,
            imageCategory,
            (progressEvent) => handleUploadProgress(progressEvent, file)
          )

        if (unmount.current) return

        results.push(response.fullUrl || response.url)
      } catch (err) {
        console.log(err)

        uploadFailed(file)

        fails.push(file)
      }
    }

    if (fails.length === 0) {
      onUploadComplete(results)

      setOpenModal(false)

      return
    }

    setHaveFailedUploads(true)

    setFailedUploads(fails)

    setUploadedFiles(results)

    setSending(false)

    toast.error(t("برخی_از_فایل_ها_آپلود_نشدند"), { autoClose: false })
  }

  const handleFinish = () => {
    onUploadComplete(uploadedFiles)

    setOpenModal(false)
  }

  useEffect(() => {
    unmount.current = false

    return () => { unmount.current = true }
  }, [])

  return (
    <div className={classes["container"]}>
      <Button
        onClick={handleDeselect}
        disabled={sending}
        color="danger"
        className={classes["delete"]}
      >
        {t("حذف_فایل_ها")}
      </Button>

      <div className={classes["row"]}>
        <Tooltip
          title={maxFilesReached
            ? t("حداکثر_maxNumberOfUploads_فایل_می_توان_انتخاب_کرد", { maxNumberOfUploads })
            : ""
          }
        >
          <span>
            <Button
              color="info"
              className={classes["button"]}
              disabled={maxFilesReached || sending}
              onClick={() => setTab("device")}
            >
              {t("افزودن_فایل")}
            </Button>
          </span>
        </Tooltip>

        {haveFailedUploads
          ?
          <Fragment>
            <Button
              color="warning"
              className={classes["button"]}
              loading={sending}
              onClick={() => handleUploadFiles(failedUploads)}
            >
              {t("تلاش_مجدد")}
              <span className={classes["number"]}>
                {failedUploads.length}
              </span>
            </Button>

            {
              uploadedFiles.length > 0 &&
              <Button
                color="success"
                className={classes["button"]}
                disabled={sending}
                onClick={handleFinish}
              >
                {t("پایان")}
                <span className={classes["number"]}>
                  {uploadedFiles.length}
                </span>
              </Button>
            }
          </Fragment>
          :
          <Button
            color="success"
            className={classes["button"]}
            loading={sending}
            onClick={() => handleUploadFiles(files)}
          >
            {t("آپلود")}
            {
              files.length > 0 &&
              <span className={classes["number"]}>
                {files.length}
              </span>
            }
          </Button>
        }
      </div>
    </div>
  )
}

export default FuwfFooter
